import { clamp, lerp, lerpAngle } from '../core/math';

/**
 * Follow camera for the 2D top-down view.
 *
 * North-up by default — a fixed circuit keeps every braking point in the same
 * place on screen, and memorising braking points is the game. When the player
 * opts into `kart-up` the view rotates so the kart's nose points to the top of
 * the screen; the rotation eases in slowly (`rotK`, far looser than the
 * position follow) so a spin or a low-speed wiggle does not whip the world
 * around exactly when the player most needs to read it.
 */
export class Camera {
  x = 0;
  y = 0;
  scale = 11; // px per metre
  viewW = 1;
  viewH = 1;
  dpr = 1;

  /** Eased view rotation, radians. 0 = north-up; non-zero only in kart-up. */
  rot = 0;
  /** When false, `rot` is held at 0 and the view never turns. */
  private kartUp = false;
  private targetHeading = 0;

  private targetScale = 11;

  static readonly SCALE_SLOW = 12.5;
  static readonly SCALE_FAST = 8.2;
  /** Speed at which the view is fully zoomed out, m/s. */
  static readonly SCALE_REF = 30;

  resize(canvas: HTMLCanvasElement): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
    }
    this.viewW = w;
    this.viewH = h;
    this.dpr = dpr;
  }

  /** `kart-up` turns the view; anything else pins it north-up. */
  setOrientation(kartUp: boolean): void {
    this.kartUp = kartUp;
    if (!kartUp) this.targetHeading = 0;
  }

  /** Ease toward the kart, pull the view back as speed rises, ease the turn. */
  follow(tx: number, ty: number, heading: number, speed: number, dt: number): void {
    const t = clamp(speed / Camera.SCALE_REF, 0, 1);
    this.targetScale = lerp(Camera.SCALE_SLOW, Camera.SCALE_FAST, t);
    const k = 1 - Math.pow(0.0015, dt);
    this.x = lerp(this.x, tx, k);
    this.y = lerp(this.y, ty, k);
    this.scale = lerp(this.scale, this.targetScale, 1 - Math.pow(0.05, dt));

    // Rotate the world by -heading - PI/2 so the kart's forward vector
    // (cos h, sin h) lands pointing straight up the screen.
    if (this.kartUp) this.targetHeading = -heading - Math.PI / 2;
    const rotK = 1 - Math.pow(0.12, dt);
    this.rot = lerpAngle(this.rot, this.targetHeading, rotK);
  }

  snap(tx: number, ty: number, heading = 0): void {
    this.x = tx;
    this.y = ty;
    this.scale = Camera.SCALE_SLOW;
    this.targetHeading = this.kartUp ? -heading - Math.PI / 2 : 0;
    this.rot = this.targetHeading;
  }

  /** Maps world metres to device pixels. Call inside a save()/restore(). */
  applyTransform(ctx: CanvasRenderingContext2D): void {
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(this.viewW / 2, this.viewH / 2);
    ctx.scale(this.scale, this.scale);
    if (this.rot) ctx.rotate(this.rot);
    ctx.translate(-this.x, -this.y);
  }

  /** Half-extents of the visible world region, plus margin, for culling. */
  visibleRadius(): number {
    return Math.hypot(this.viewW, this.viewH) / (2 * this.scale) + 20;
  }

  /** World metres to CSS pixels from the canvas top-left, rotation included. */
  worldToScreen(x: number, y: number): { x: number; y: number } {
    const dx = (x - this.x) * this.scale;
    const dy = (y - this.y) * this.scale;
    const c = Math.cos(this.rot);
    const s = Math.sin(this.rot);
    return {
      x: c * dx - s * dy + this.viewW / 2,
      y: s * dx + c * dy + this.viewH / 2,
    };
  }

  isVisible(x: number, y: number, pad = 0): boolean {
    // Rotate the offset into view space so culling stays correct in kart-up.
    const dx = x - this.x;
    const dy = y - this.y;
    const c = Math.cos(this.rot);
    const s = Math.sin(this.rot);
    const vx = c * dx - s * dy;
    const vy = s * dx + c * dy;
    const halfW = this.viewW / (2 * this.scale) + pad;
    const halfH = this.viewH / (2 * this.scale) + pad;
    return Math.abs(vx) <= halfW && Math.abs(vy) <= halfH;
  }
}
